import { useEffect, useRef, useState } from "react";
import BarcodeReaderComponent from "../../components/BarcodeReader";
import { useDrawerContext } from "../../context/drawerContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useUserContext } from "../../context/userContext";
import { useHeaderContext } from "../../context/headerContext";
import Button from "../../components/Button";
import BandCard from "../../components/BandCard";
import CloseIcon from "/public/images/icons/close.svg?react";
import Tick from "/public/images/icons/greenTick.svg?react";
import Cross from "/public/images/icons/redCross.svg?react";
import fetchWithRefresh from "../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../utils/LogErrors";

export default function AssignBandScan() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const participant = state?.participant;
  const bookingId = state?.bookingId;
  const participantId = state?.participantId;
  const allParticipants = state?.allParticipants;

  const { setOpen, setDrawerContent } = useDrawerContext();
  const { user, logout, updateTokens } = useUserContext();
  const { setTitle, setBackButton } = useHeaderContext();
  const [isInitialised, setIsInitialised] = useState(false);
  const [confirmedBand, setConfirmedBand] = useState(null);
  const [bookingIn, setBookingIn] = useState(false);
  const scanningRef = useRef(false);
  const cameraRef = useRef(null);

  const resetToScanner = () => {
    scanningRef.current = false;
    setIsInitialised(false);
    setConfirmedBand(null);
  };

  useEffect(() => {
    return () => {
      cameraRef.current?.destroy();
    };
  }, []);

  useEffect(() => {
    setTitle("Assign Band");
    setBackButton(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isInitialised) return;
    setOpen(true);
    setDrawerContent(
      <div className="w-full scan-drawer">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">Assign Band</h2>
          <CloseIcon className="cursor-pointer" onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col gap-2 justify-stretch">
          <h3 className="text-[24px]">Scan the band to assign</h3>
          <p className="text-sportsDarkGrey">
            Point your camera at the QR code on the band to assign it to{" "}
            <span className="font-semibold text-sportsBlack">{participant?.participantName}</span>.
          </p>
        </div>
      </div>
    );
  }, [isInitialised]); // eslint-disable-line react-hooks/exhaustive-deps

  async function finishScanning(detections) {
    if (scanningRef.current) return;
    scanningRef.current = true;
    setOpen(false);

    await Sentry.startSpan({ name: "Assign Band Scan", op: "qr.scan" }, async (span) => {
      try {
        const url = detections[0].data;
        let iceid = null;

        if (url.includes("https://iceid.app?")) {
          iceid = url.split("https://iceid.app?")[1];
        } else if (url.includes("https://iceid.app/?")) {
          iceid = url.split("https://iceid.app/?")[1];
        } else if (url.includes("https://iceid.app/")) {
          iceid = url.split("https://iceid.app/")[1];
        }

        // Pass bookingParticipantId if available
        const bpId = participant?.bookingParticipantId;
        const assignReq = await fetchWithRefresh(
          `${import.meta.env.VITE_API_URL}scan/bookingbandscan?scanId=${iceid}&bookingId=${bookingId}${bpId != null ? `&bookingParticipantId=${bpId}` : ""}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + user.token,
            },
          },
          logout, updateTokens, (message) => { toast.info(message); }
        );

        span.setAttribute("http.status_code", assignReq.status);

        if (!assignReq.ok) {
          const errorText = await assignReq.text();
          LogErrors(user, logout, updateTokens, `Failed to assign band. Status: ${assignReq.status}, Response: ${errorText}`);
          showError(errorText || "Unable to assign the band. Please try again.");
          return;
        }

        span.setStatus("ok");
        await cameraRef.current?.destroy();
        const bandData = await assignReq.json();
        showConfirm(bandData);
      } catch (error) {
        span.setStatus("error");
        Sentry.captureException(error);
        LogErrors(user, logout, updateTokens, "Error assigning band: " + error);
        showError("Something went wrong. Please try again.");
      } finally {
        span.end();
        scanningRef.current = false;
      }
    });
  }

  function showConfirm(bandData) {
    setConfirmedBand(bandData);
  }

  async function handleBookIn(bandData) {
    setBookingIn(true);
    try {
      const bpId = participant?.bookingParticipantId;
      const response = await fetchWithRefresh(
        `${import.meta.env.VITE_API_URL}scan/bookin?scanId=${bandData.scanId}&bookingId=${bandData.bookingId}${bpId != null ? `&bookingParticipantId=${bpId}` : ""}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + user.token,
          },
        },
        logout, updateTokens, (message) => { toast.info(message); }
      );
      if (!response.ok) {
        const errorText = await response.text();
        LogErrors(user, logout, updateTokens, `Failed to book in. Status: ${response.status}, Response: ${errorText}`);
        showError(errorText || "Unable to complete booking. Please try again.");
        return;
      }
      const allBooked = allParticipants?.length > 0 &&
        allParticipants.every((p) =>
          p.bookingParticipantId === participant?.bookingParticipantId ||
          p.isBookedIn === 1 ||
          p.bandStatus === "active"
        );
      showSuccess(allBooked);
    } catch (error) {
      Sentry.captureException(error);
      LogErrors(user, logout, updateTokens, "Error booking in: " + error);
      showError("Something went wrong. Please try again.");
    } finally {
      setBookingIn(false);
    }
  }

  function showSuccess(allBooked = false) {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">Success</h2>
          <CloseIcon className="cursor-pointer" onClick={() => { setOpen(false); navigate(`/bookings/${bookingId}`, { replace: true }); }} />
        </div>
        {allBooked ? (
          <>
            <div className="mt-6 flex flex-col gap-2 justify-stretch">
              <h3 className="text-[24px]"> All participants have now been booked in</h3>
            </div>
            <div className="mt-6 flex flex-col gap-2">
              <Button variant="primary" onClick={() => { setOpen(false); navigate(`/bookings`, { replace: true }); }}>
                Back To All Bookings
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="mt-6 flex flex-col gap-2 justify-stretch">
              <h3 className="text-[24px]">Booked in successfully</h3>
              <p className="text-sportsDarkGrey">The participant has been checked in.</p>
            </div>
            <div className="mt-6 flex flex-col gap-2">
              <Button variant="primary" onClick={() => { setOpen(false); navigate(`/bookings/${bookingId}`, { replace: true }); }}>
                Add Another Participant
              </Button>
              <Button variant="secondary" onClick={() => { setOpen(false); navigate(`/bookings/${bookingId}`, { replace: true }); }}>
                Go Back
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  function showError(message) {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">Error</h2>
          <CloseIcon className="cursor-pointer" onClick={() => { setOpen(false); scanningRef.current = false; }} />
        </div>
        <div className="mt-6 flex flex-col gap-2 justify-stretch">
          <h3 className="text-[24px]">Could not assign band</h3>
          <p className="text-sportsDarkGrey">{message}</p>
          <Button variant="primary" onClick={() => { setOpen(false); scanningRef.current = false; }}>
            Try Again
          </Button>
          <Button variant="quaternary" onClick={() => navigateBack(false)}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  function navigateBack(assigned) {
    setOpen(false);
    navigate(`/bookings/${bookingId}`, { replace: true });
  }

  function onDetected(detections) {
    sessionStorage.setItem("lastCodeScanned", detections[0].data);
    finishScanning(detections);
  }

  if (confirmedBand) {
    const isGuestBand = confirmedBand.isGuestBand === 1;
    const isSingleUseBand = confirmedBand.isSingleUseBand === 1;
    const bandForCard = {
      BandColour: isGuestBand ? "GREY" : confirmedBand.bandColour,
      BandImageURL: confirmedBand.bandImageURL,
      FriendlyName: confirmedBand.friendlyName,
      BandStateVerbose: "ASSIGNED",
      IsGuestBand: isGuestBand,
      IsSingleUseBand: isSingleUseBand,
    };
    const participantForCard = {
      ParticipantName: confirmedBand.participantName,
    };
    const allMilestonesMet = !confirmedBand.milestoneCriteria?.length ||
      confirmedBand.milestoneCriteria.every((m) => m.hasMilestone === 1);
    const bookInDisabled = bookingIn || !allMilestonesMet;

    return (
      <div className="p-[24px] bg-sportsLightGrey min-h-screen flex flex-col gap-[30px]">
        <BandCard band={bandForCard} participant={participantForCard} size="sm" href="/bookings/assign-band/scan" label={isGuestBand ? "GUEST BAND" : undefined} />

        {confirmedBand.participantName && (
          <div>
            <h3>{confirmedBand.participantName}</h3>
          </div>
        )}

        {confirmedBand.milestoneCriteria?.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="font-semibold text-sm">Hard Stops</p>
            {confirmedBand.milestoneCriteria.map((m) => (
              <div key={m.bookableThingMilestoneCriteriaId} className="flex flex-row items-center gap-x-3">
                {m.hasMilestone === 1 ? <Tick /> : <Cross />}
                <p className={`text-sm ${m.hasMilestone === 1 ? "text-[#47B405]" : "text-[#DF3327]"}`}>{m.milestoneName}</p>
              </div>
            ))}
          </div>
        )}

        {confirmedBand.adHocCriteria?.length > 0 && (
          <div className="flex flex-col gap-1">
            <p className="font-semibold text-sm">Soft Stops</p>
            {confirmedBand.adHocCriteria.map((c) => (
              <p key={c.criteriaId} className="text-sm text-sportsDarkGrey">{c.criteriaTitle}</p>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-3">
          {bookInDisabled && !bookingIn && !allMilestonesMet && (
            <p className="text-red-500 text-sm">Can't book participant in - see hard stops above</p>
          )}
          <Button
            variant="primary"
            status={bookInDisabled ? "disabled" : "null"}
            onClick={() => handleBookIn(confirmedBand)}
            disabled={bookInDisabled}
          >
            {bookingIn ? "Booking In..." : "Book In"}
          </Button>
          <Button variant="quaternary" onClick={resetToScanner}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <BarcodeReaderComponent ref={cameraRef} pageSetIsInitialised={() => setIsInitialised(true)} onDetected={onDetected} />
  );
}
